import { Box, Section } from '@factorialco/deprecated-design-system'
import { A3nomIntegrationPageDocument } from 'generated/resources/graphql'

import { useAsyncData } from 'modules/core/hooks/useAsyncData'
import { useQuery } from 'modules/core/hooks/useQuery'
import i18n from 'modules/core/lib/i18n'
import companies from 'modules/core/store/collections/companies'
import legalEntities from 'modules/core/store/collections/legalEntities'

import type { Codeable, ColumnNames } from 'modules/app_store/pages/SettingsPage/Apps/common/types'
import taxonomies from 'modules/contracts/store/collections/taxonomies'
import { ResponsivePage } from 'modules/layout/components/ResponsivePage'
import locations from 'modules/locations/store/collections/locations'
import CodesBasedOnSelectors from 'modules/payroll_integrations/components/Content/CodesBasedOnSelectors'
import Table from 'modules/payroll_integrations/components/Table'
import PayrollIntegration from 'modules/payroll_integrations/lib/catalog'

import PayrollPaywallGuard from '../../PayrollPaywallGuard'
import EmployeeCodeConfiguration from './EmployeeCodeConfiguration'

const A3nomIntegration = () => {
  const INTEGRATION_NAME = PayrollIntegration.a3nom

  const { data, loading, error } = useQuery(A3nomIntegrationPageDocument, {
    variables: {
      companyId: companies.current.id,
    },
  })

  const hasDevCompensations =
    data?.features?.featuresConnection.nodes.some(
      (feature) => feature.name === 'dev_compensations' && feature.enabled
    ) ?? false

  const codeables: Record<string, Codeable> = {
    company: {
      position: 1,
      collection: [companies.current],
      class: 'Company',
      label: i18n.t(
        'app_store_v2.app_integrations.payroll_integrations_paierh.settings.form.concept.company'
      ),
    },
    legalEntity: {
      position: 2,
      collection: legalEntities,
      class: 'LegalEntity',
      label: i18n.t(
        'app_store_v2.app_integrations.payroll_integrations_paierh.settings.form.concept.legal_entity'
      ),
    },
    location: {
      position: 4,
      collection: locations,
      class: 'Location',
      label: i18n.t(
        'app_store_v2.app_integrations.payroll_integrations_a3innuva.settings.form.concept.location'
      ),
    },
  }

  if (!hasDevCompensations) {
    codeables.taxonomy = {
      position: 3,
      collection: taxonomies.actives,
      class: 'Contracts::Compensations::Taxonomy',
      label: i18n.t(
        'app_store_v2.app_integrations.payroll_integrations_a3nom.settings.form.concept.taxonomy'
      ),
    }
  }

  const COLUMN_NAMES: ColumnNames = [
    {
      name: i18n.t(
        'app_store_v2.app_integrations.payroll_integrations_a3nom.settings.form.header.concept'
      ),
      colSpan: 4,
      attribute: 'concept',
    },
    {
      name: i18n.t(
        'app_store_v2.app_integrations.payroll_integrations_a3nom.settings.form.header.value'
      ),
      colSpan: 4,
      attribute: 'value',
    },
  ]

  const { loading: loadingCollections } = useAsyncData(() => {
    return Promise.all([taxonomies.fetch()])
  }, [])

  const sectionDescription = hasDevCompensations
    ? i18n.t(
        'app_store_v2.app_integrations.payroll_integrations_a3nom.settings.form.description_without_additional_compensations'
      )
    : i18n.t('app_store_v2.app_integrations.payroll_integrations_a3nom.settings.form.description')
  return (
    <PayrollPaywallGuard>
      <ResponsivePage.Body loading={loading || loadingCollections} error={error}>
        <EmployeeCodeConfiguration />

        <CodesBasedOnSelectors integration_name={INTEGRATION_NAME} />

        <>
          <Section
            description={sectionDescription}
            illustration="bookmark"
            title={i18n.t(
              'app_store_v2.app_integrations.payroll_integrations_a3nom.settings.form.title'
            )}
          />
          <Box width="full" alignItems="center" gap="s32" paddingY="s48" paddingX="s64">
            <Table
              columnNames={COLUMN_NAMES}
              codeables={codeables}
              integration={INTEGRATION_NAME}
            />
          </Box>
        </>
      </ResponsivePage.Body>
    </PayrollPaywallGuard>
  )
}

export default A3nomIntegration
